import { isEqual } from 'lodash';

import {
  ClientPresentationBundleConfigurationBase,
  ClientPresentationV2Bundle,
} from '../../types/ClientPresentation.PresentationBundleType';
import {
  BundleConfigurationPayload,
  BundleSaveDescriptor,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';

/**
 * How one kind's flat configuration is read, compared, and wrapped.
 *
 * "Flat" means scalars only — no child rows. Those kinds differ in nothing but
 * which field they live on, which slot they write, and which scalars they carry,
 * so a spec plus this factory replaces a descriptor file each.
 */
export interface FlatConfigSpec<
  TConfig extends ClientPresentationBundleConfigurationBase,
  TUpdate,
> {
  /** The bundle field this configuration lives on. */
  field: keyof ClientPresentationV2Bundle;
  /** Reads the configuration off a bundle. */
  read: (bundle: ClientPresentationV2Bundle) => TConfig | undefined;
  /** The scalars that changed, or null when none did. */
  diff: (
    previous: TConfig,
    next: TConfig,
    previousBundle: ClientPresentationV2Bundle,
    nextBundle: ClientPresentationV2Bundle
  ) => TUpdate | null;
  /**
   * Wraps an update in this kind's own slot. A closure rather than a slot name,
   * because assigning through a key variable widens the write to the intersection
   * of every slot's type.
   */
  toPayload: (id: string, update: TUpdate) => BundleConfigurationPayload;
}

/**
 * Whether a configuration row can be written at all, given how it changed.
 *
 * Shared by every descriptor, flat or nested: the answer depends only on whether
 * both sides have a configuration, whether it changed, and whether it has a
 * server row.
 */
export function canWriteOwnedConfig<
  TConfig extends ClientPresentationBundleConfigurationBase,
>(
  read: (bundle: ClientPresentationV2Bundle) => TConfig | undefined,
  previous: ClientPresentationV2Bundle,
  next: ClientPresentationV2Bundle
): boolean {
  const before = read(previous);
  const after = read(next);
  // Neither side has one, so there is nothing to write.
  if (!before && !after) return true;
  // One side has one: the row has to be created or deleted, and a diff can only
  // update. Neither can be written.
  if (!before || !after) return false;
  // An unchanged local-only configuration requires no write. If it changed, an
  // incremental update requires the server row identity.
  return isEqual(before, after) || Boolean(after.id);
}

export function createFlatConfigDescriptor<
  TConfig extends ClientPresentationBundleConfigurationBase,
  TUpdate,
>(spec: FlatConfigSpec<TConfig, TUpdate>): BundleSaveDescriptor {
  function canWrite(
    previous: ClientPresentationV2Bundle,
    next: ClientPresentationV2Bundle
  ): boolean {
    return canWriteOwnedConfig(spec.read, previous, next);
  }

  function diffConfiguration({
    previous,
    next,
  }: DiffBundleConfigurationArgs): BundleConfigurationPayload | null {
    const before = spec.read(previous);
    const after = spec.read(next);
    // `canWrite` refused every other combination, so both are present here.
    if (!before || !after?.id) return null;

    const update = spec.diff(before, after, previous, next);
    return update ? spec.toPayload(after.id, update) : null;
  }

  return { ownedFields: [spec.field], canWrite, diffConfiguration };
}
